<script setup lang="ts">
import { useAttrs, computed } from 'vue';
import type { InputNumberInputEvent } from 'primevue/inputnumber'; // https://primevue.org/inputnumber/

defineOptions({
    name: 'IdeoFormNumber',
    inheritAttrs: false
});

const { $i18n } = useLocalization();

const model = defineModel<number>();

const props = withDefaults(defineProps<{
    locale?: string;
    fluid?: boolean;
    clearable?: boolean;
    min?: number;
    // ... additional props: https://primevue.org/inputnumber/#api.inputnumber.props
}>(), {
    locale: 'en-US',
    fluid: true,
    clearable: false,
    min: 0
});

const emit = defineEmits<{
    (e: 'input', value: number): void;
}>();

const rootRef = ref(null);

const attrs = useAttrs() as any;
const locale = computed(() => props.locale ?? $i18n.locale());

const resetValue = (): void =>
{
    model.value = 0;
};

const handleInput = (e: InputNumberInputEvent): void =>
{
    model.value = e.value as number;
    emit('input', e.value as number);
};

const handleUpdate = (value: number | null) =>
{
    if (value === null)
    {
        model.value = props.min;
        emit('input', props.min);
    }
};

watch(model, () =>
{
    rootRef.value.querySelector('input')?.dispatchEvent(new Event('input', { bubbles: true }));
});
</script>

<template>
    <div ref="rootRef">
        <PrimeInputGroup>
            <PrimeInputNumber
                v-model="model"
                :locale="locale"
                :fluid="fluid"
                v-bind="attrs"
                @input="handleInput"
                @update:model-value="handleUpdate"
            />
            <PrimeInputGroupAddon v-if="clearable">
                <PrimeButton icon="fas fa-fw fa-times" severity="secondary" @click.prevent="resetValue" />
            </PrimeInputGroupAddon>
        </PrimeInputGroup>
    </div>
</template>
